import { Component, DestroyRef, OnInit, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { IglesiasService } from '../../../../core/services/iglesias.service';
import { UserStore } from '../../../../core/services/user-store.service';
import { UsersService } from '../../../../core/services/users.service';
import { UserListItem } from '../../../../shared/interfaces/user.interface';
import {
  AgregarPersonaDatosBasicos,
  AgregarPersonaLiderOption,
  AgregarPersonaPasoConfirmacion,
} from '../../agregar-persona-form.interface';
import { resolveMiLider } from '../../helpers/build-create-user-payload';
import { toPlainLowercase } from '../../../../shared/helpers/nombre-texto.helper';

const ID_LIDER_NINGUNO = 'sin lider';
const ID_LIDER_EVADIR = 'evadir';
const MINISTERIO_NINGUNO = 'ninguno';

@Component({
  selector: 'app-agregar-persona-paso-confirmacion',
  standalone: true,
  imports: [ReactiveFormsModule, Button, InputText, Select, Checkbox, Skeleton],
  templateUrl: './paso-confirmacion.component.html',
  styleUrl: './paso-confirmacion.component.scss',
})
export class AgregarPersonaPasoConfirmacionComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStore = inject(UserStore);
  private readonly iglesiasService = inject(IglesiasService);
  private readonly usersService = inject(UsersService);

  /** Datos del paso 1: para preview de mailAccess si se marcan privilegios. */
  readonly datosBasicos = input<AgregarPersonaDatosBasicos | null>(null);
  readonly saving = input(false);
  /** Prefill en modo editar. */
  readonly initial = input<AgregarPersonaPasoConfirmacion | null>(null);

  readonly back = output<void>();
  readonly save = output<AgregarPersonaPasoConfirmacion>();

  readonly loading = signal(true);
  readonly lideres = signal<UserListItem[]>([]);
  /** Nombre y apellido de la sesión, para cuando el líder elegido es «Yo». */
  private readonly nombrePropio = signal('');
  readonly ministerios = signal<string[]>([]);
  readonly setLevel = signal(0);

  readonly form = this.fb.nonNullable.group({
    direccion: [''],
    ciudad: [''],
    email: [''],
    idLider: [ID_LIDER_NINGUNO],
    ministerio: [MINISTERIO_NINGUNO],
    nivelPersonal: [0],
    admin: [false],
    lider: [false],
    contador: [false],
  });

  readonly privilegiosActivos = signal(false);

  private readonly _applyInitial = effect(() => {
    const data = this.initial();
    if (!data) {
      return;
    }
    this.form.patchValue({
      direccion: data.direccion,
      ciudad: data.ciudad,
      email: data.email,
      idLider: data.idLider || ID_LIDER_NINGUNO,
      ministerio: data.ministerio || MINISTERIO_NINGUNO,
      nivelPersonal: data.nivelPersonal ?? 0,
      admin: data.admin,
      lider: data.lider,
      contador: data.contador,
    });
    if (data.admin || data.lider || data.contador) {
      this.privilegiosActivos.set(true);
    }
  });

  readonly isAdmin = this.userStore.isAdmin;
  readonly isLider = this.userStore.isLider;
  readonly isContador = this.userStore.isContador;

  /** Privilegios solo visibles para admin (mostrarAdmin en Mayorap2). */
  readonly mostrarPrivilegios = computed(() => this.isAdmin());

  /** Nivel personal: admin y setLevel > 0. */
  readonly mostrarNivelPersonal = computed(
    () => this.isAdmin() && this.setLevel() > 0,
  );

  /** Contador puro: label "Líder" y solo Ninguno. */
  readonly esContadorPuro = computed(
    () => this.isContador() && !this.isAdmin() && !this.isLider(),
  );

  readonly mostrarAsignarLider = computed(() => {
    if (this.isAdmin()) {
      return true;
    }
    if (this.isLider() && !this.isAdmin()) {
      return true;
    }
    return this.esContadorPuro();
  });

  readonly liderOptions = computed((): AgregarPersonaLiderOption[] => {
    const options: AgregarPersonaLiderOption[] = [];
    const yoId = this.userStore.user()?._id;
    const lideres = this.lideres();

    if (this.isAdmin() && !this.isLider()) {
      options.push({ label: 'Evadir', value: ID_LIDER_EVADIR });
      options.push({ label: 'Ninguno', value: ID_LIDER_NINGUNO });
      for (const lider of lideres) {
        options.push({
          label: `${lider.nombre} ${lider.apellido}`.trim(),
          value: lider.id,
        });
      }
      return options;
    }

    if (this.isAdmin() && this.isLider()) {
      options.push({ label: 'Evadir', value: ID_LIDER_EVADIR });
      options.push({ label: 'Ninguno', value: ID_LIDER_NINGUNO });
      if (yoId) {
        options.push({ label: 'Yo', value: yoId });
      }
      for (const lider of lideres) {
        if (lider.id === yoId) {
          continue;
        }
        options.push({
          label: `${lider.nombre} ${lider.apellido}`.trim(),
          value: lider.id,
        });
      }
      return options;
    }

    if (this.isLider() && !this.isAdmin()) {
      options.push({ label: 'Ninguno', value: ID_LIDER_NINGUNO });
      if (yoId) {
        options.push({ label: 'Yo', value: yoId });
      }
      return options;
    }

    if (this.esContadorPuro()) {
      options.push({ label: 'Ninguno', value: ID_LIDER_NINGUNO });
      return options;
    }

    options.push({ label: 'Ninguno', value: ID_LIDER_NINGUNO });
    return options;
  });

  readonly ministerioOptions = computed(() => {
    const fromIglesia = this.ministerios().map((nombre) => ({
      label: nombre,
      value: nombre,
    }));
    return [{ label: 'Ninguno', value: MINISTERIO_NINGUNO }, ...fromIglesia];
  });

  readonly nivelOptions = [
    { label: 'Nivel 0', value: 0 },
    { label: 'Nivel 1', value: 1 },
    { label: 'Nivel 2', value: 2 },
    { label: 'Nivel 3', value: 3 },
    { label: 'Nivel 4', value: 4 },
  ];


  readonly mostrarMailPreview = computed(
    () => this.mostrarPrivilegios() && this.privilegiosActivos(),
  );

  readonly mailAccessPreview = computed(() => {
    const datos = this.datosBasicos();
    const iglesia = this.userStore.iglesia();
    if (!datos || !iglesia) {
      return '';
    }
    const local = `${datos.nombre}${datos.nombre2 ?? ''}${datos.apellido}`
      .replace(/\s+/g, '')
      .toLowerCase();
    return `${local}@${iglesia}.com`;
  });

  ngOnInit(): void {
    this.cargarDatosIglesiaYLideres();
    this.syncPrivilegioPreview();
    this.sanitizeDireccionInput();
  }

  private sanitizeDireccionInput(): void {
    this.form.controls.direccion.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const next = toPlainLowercase(value ?? '');
        if (next !== value) {
          this.form.controls.direccion.setValue(next, { emitEvent: false });
        }
      });
  }

  onSubmit(): void {
    const raw = this.form.getRawValue();
    const payload: AgregarPersonaPasoConfirmacion = {
      direccion: toPlainLowercase(raw.direccion.trim()),
      ciudad: raw.ciudad.trim(),
      email: raw.email.trim(),
      idLider: raw.idLider,
      miLider: resolveMiLider(
        raw.idLider,
        this.userStore.user(),
        this.lideres(),
        this.nombrePropio(),
      ),
      ministerio: raw.ministerio,
      nivelPersonal: this.mostrarNivelPersonal() ? Number(raw.nivelPersonal) : 0,
      admin: this.mostrarPrivilegios() ? raw.admin : false,
      lider: this.mostrarPrivilegios() ? raw.lider : false,
      contador: this.mostrarPrivilegios() ? raw.contador : false,
    };
    this.save.emit(payload);
  }

  private cargarDatosIglesiaYLideres(): void {
    const iglesia$ = this.iglesiasService.getCurrentIglesia().pipe(
      catchError(() => of(null)),
    );
    const lideres$ = this.isAdmin()
      ? this.usersService.getLideres()
      : of([] as UserListItem[]);
    const yo$ = this.isLider()
      ? this.usersService.getCurrentUser().pipe(
          map((response) => `${response.data.nombre} ${response.data.apellido}`.trim()),
          catchError(() => of('')),
        )
      : of('');

    forkJoin({ iglesia: iglesia$, lideres: lideres$, yo: yo$ })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ iglesia, lideres, yo }) => {
        if (iglesia) {
          this.ministerios.set(iglesia.ministerios ?? []);
          this.setLevel.set(iglesia.setLevel ?? 0);
        }
        this.lideres.set(lideres);
        this.nombrePropio.set(yo);
        this.loading.set(false);
      });
  }

  private syncPrivilegioPreview(): void {
    const sync = (): void => {
      const value = this.form.getRawValue();
      this.privilegiosActivos.set(value.admin || value.lider || value.contador);
    };
    sync();
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => sync());
  }
}